import { Box, GlobalStyles, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
  axios,
  PageNames,
  useSessionInfo,
  DEFAULT_ECOSYSTEM_NAME,
  useSessionInfoOnSuccess,
  generateTestIds,
  useOnboardingStore,
  useAppStore,
  FixedHeadersSpacer,
  useNavbarStore,
  PageLoader,
  localStorageUtil,
  LocalStorageKey,
  useLocationTrackingForCreateEnvironment,
  useCurrentUserInfo,
  useIsOnline,
  OfflineErrorView,
} from '@diana/core';
import { useTranslation } from 'react-i18next';
import { ONBOARDING_PAGE_NS } from './i18n';
import { useOnboardingPageSteps } from './useOnboardingPageSteps';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OnboardingStepper } from './OnboardingStepper';
import { OnboardingButtons } from './OnboardingButtons';
import { useCompanyResearch } from './useCompanyResearch';

export const OnboardingTestIds = generateTestIds('Onboarding', [
  'companyName',
  'ecosystemName',
  'industry',
  'websiteUrl',
  'goBack',
  'onCancel',
  'continue',
]);

export const OnboardingPage = () => {
  const { palette, breakpoints } = useTheme();
  const { t } = useTranslation(ONBOARDING_PAGE_NS);
  const isOnline = useIsOnline();
  const onSessionInfoSuccess = useSessionInfoOnSuccess();
  const infoFromStore = useCurrentUserInfo();
  const bannerRef = useRef<HTMLDivElement>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const isSmallHeight = useRef<boolean>(false);
  const isLargeDevice = useMediaQuery(breakpoints.up(1100));

  axios.setEnvironment(DEFAULT_ECOSYSTEM_NAME);
  const anonId = localStorageUtil.getItem({ key: LocalStorageKey.anonId }) ?? '';
  useLocationTrackingForCreateEnvironment({ anonId }, { trackOnMount: true });

  const [isInfoUpdated, setIsInfoUpdated] = useState<boolean>(false);

  const navbarHeight = useNavbarStore((store) => store.getHeight());
  const setCurrentPageName = useAppStore((store) => store.setCurrentPageName);
  const setCurrentPageTitle = useAppStore((store) => store.setCurrentPageTitle);
  const currentStepIndex = useOnboardingStore((store) => store.stepIndex);
  const research = useOnboardingStore((store) => store.research);

  const { mutate: researchCompany, isPending: isResearchingCompany } = useCompanyResearch({
    saveAiAreas: true,
  });
  const {
    data: info,
    isFetchedAfterMount,
    isLoading: isLoadingInfo,
  } = useSessionInfo({ ecosystem: DEFAULT_ECOSYSTEM_NAME });

  const steps = useOnboardingPageSteps();
  const canCreateEcosystem = info?.features?.environmentCreate ?? false;
  const stepsLength = steps.length;
  const {
    component,
    banner,
    onSubmit,
    onCancel,
    isLoading: submitLoadingState,
    disableSubmit,
  } = useMemo(() => {
    return steps[currentStepIndex];
  }, [steps, currentStepIndex]);
  const isContentLoading =
    !isInfoUpdated || isLoadingInfo || isResearchingCompany || !info || !research;

  const handleHeights = useCallback(() => {
    if (mainContentRef.current && bannerRef.current && isLargeDevice) {
      const mainContentHeight = mainContentRef.current.clientHeight + 144; // padding
      const bannerHeight = bannerRef.current.clientHeight;

      if (bannerHeight <= mainContentHeight && !isSmallHeight.current) {
        isSmallHeight.current = true;
        bannerRef.current.style.minHeight = `${mainContentHeight}px`;
      }
      if (bannerHeight > mainContentHeight && isSmallHeight.current) {
        isSmallHeight.current = false;
        bannerRef.current.style.minHeight = 'auto';
      }
    }
  }, [mainContentRef, bannerRef, isLargeDevice]);

  useEffect(() => {
    handleHeights();

    window.addEventListener('resize', () => handleHeights());
    return () => {
      window.removeEventListener('resize', () => handleHeights());
    };
  }, [handleHeights]);

  useEffect(() => {
    if (!!info && !research) {
      researchCompany({ email: info.email, companyName: info.companyName });
    }
  }, [info, research, researchCompany]);

  useEffect(() => {
    if (infoFromStore?.environmentSettings?.id === info?.environmentSettings?.id) {
      setIsInfoUpdated(true);
    }
  }, [info, infoFromStore, setIsInfoUpdated]);

  useEffect(() => {
    if (!!info && !canCreateEcosystem) {
      window.location.assign('https://www.rise-x.io/pricing');
    }
  }, [info, canCreateEcosystem]);

  useEffect(() => {
    setCurrentPageTitle(t('title'));
    setCurrentPageName(PageNames.CREATE_ENVIRONMENT);

    if (!!info && !isFetchedAfterMount) {
      onSessionInfoSuccess(info);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isOnline) return <OfflineErrorView />;

  return (
    <>
      <GlobalStyles
        styles={{
          body: {
            backgroundColor:
              palette.mode === 'dark' ? palette.background.default : palette.background.paper,
          },
        }}
      />
      <Box>
        <FixedHeadersSpacer />
        <PageLoader isLoading={isContentLoading} />
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          gap={4}
          p={isLargeDevice ? 4 : 2}
          sx={{
            width: '100%',
            height:
              !isSmallHeight.current && isLargeDevice ? `calc(100dvh - ${navbarHeight}px)` : '100%',
            opacity: isContentLoading ? 0 : 1,
          }}
        >
          <Stack
            flex={isLargeDevice ? '50%' : '100%'}
            alignItems="center"
            py={isLargeDevice ? 9 : 6}
          >
            <Stack ref={mainContentRef} gap={3} sx={{ maxWidth: 375, width: '100%' }}>
              <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
                <OnboardingStepper length={stepsLength} currentIndex={currentStepIndex} />
                <Typography variant="body2" color={palette.text.secondary}>
                  {t('stepper', { currentStep: currentStepIndex + 1, totalSteps: stepsLength })}
                </Typography>
              </Stack>
              {component}
              <Box mt={1}>
                <OnboardingButtons
                  stepsLength={stepsLength}
                  onSubmit={onSubmit}
                  onCancel={onCancel}
                  isLoading={submitLoadingState}
                  disableSubmitButton={disableSubmit}
                />
              </Box>
            </Stack>
          </Stack>
          {isLargeDevice ? (
            <Stack flex="50%">
              <Stack
                ref={bannerRef}
                gap={4}
                sx={{
                  backgroundColor:
                    palette.mode === 'dark' ? palette.background.paper : palette.background.default,
                  borderRadius: 6,
                  height: '100%',
                  minWidth: 650,
                  maxWidth: 650,
                  overflow: 'hidden',
                }}
              >
                <Stack gap={2} pt={9} px={7.5}>
                  <Typography variant="h3">{banner.title}</Typography>
                  <Typography color={palette.text.secondary}>{banner.description}</Typography>
                </Stack>
                <Box pl={7.5} width="100%" height="100%">
                  {banner.illustration}
                </Box>
              </Stack>
            </Stack>
          ) : null}
        </Stack>
      </Box>
    </>
  );
};
